/**
 * LandingPage - Public marketing page for RISKEE
 * 
 * One-time purchase model: $99 AUD
 * Route: /
 */
import React, { useState } from 'react';
import { Button } from '../components/ui/button';
import { 
  FileText, AlertTriangle, ClipboardCheck, CheckCircle, 
  FolderOpen, Smartphone, BarChart3, ArrowRight, Play,
  File, Table, CreditCard, Building
} from 'lucide-react';
import EnterpriseContactModal from '../components/EnterpriseContactModal';
import '../components/EnterpriseContactModal.css';
import './LandingPage.css';

const LandingPage = ({ onGetStarted, onTryDemo }) => {
  const [showEnterpriseModal, setShowEnterpriseModal] = useState(false);
  
  const features = [
    { icon: FileText, name: 'Incident Reporting' },
    { icon: AlertTriangle, name: 'Hazard Tracking' },
    { icon: ClipboardCheck, name: 'Workplace Inspections' },
    { icon: CheckCircle, name: 'Corrective Actions' },
    { icon: FolderOpen, name: 'Safety Records' },
    { icon: Smartphone, name: 'Mobile Access' },
    { icon: BarChart3, name: 'Simple Reporting' },
  ];

  const problems = [
    { icon: File, text: 'Paper gets lost.' },
    { icon: Table, text: 'Spreadsheets become messy.' },
    { icon: CreditCard, text: 'Subscriptions become expensive.' },
  ];

  const expertise = [
    'Risk Management',
    'Investigations',
    'Governance',
    'Compliance',
    'Operational Safety',
  ];

  return (
    <div className="landing-page" data-testid="landing-page">
      {/* Header */}
      <header className="landing-header">
        <div className="landing-header-content">
          <div className="landing-logo">
            <img src="/riskee_logo.png" alt="RISKEE" className="landing-logo-img" />
            <span className="landing-logo-text">RISKEE</span>
          </div>
          <Button 
            variant="outline" 
            size="sm" 
            onClick={onGetStarted}
            data-testid="header-login-btn"
          >
            Login
          </Button>
        </div>
      </header>

      {/* Hero Section */}
      <section className="landing-hero">
        <div className="landing-hero-content">
          <h1>Safety Shouldn&apos;t Be Expensive</h1>
          <p className="hero-subtitle">
            Keep your safety records organised without expensive subscriptions, 
            complicated software or endless paperwork.
          </p>
          
          <div className="hero-features">
            <div className="hero-feature-col">
              <span>Record incidents.</span>
              <span>Track hazards.</span>
              <span>Complete inspections.</span>
            </div>
            <div className="hero-feature-col">
              <span>Assign actions.</span>
              <span>Stay organised.</span>
            </div>
          </div>

          <div className="hero-value">
            <span>One payment.</span>
            <span>Own it.</span>
            <span>Use it.</span>
          </div>

          <div className="hero-cta-group">
            <Button 
              size="lg" 
              className="hero-cta"
              onClick={onGetStarted}
              data-testid="hero-download-btn"
            >
              Download RISKEE
              <ArrowRight size={20} />
            </Button>
            <Button 
              size="lg" 
              variant="outline"
              className="hero-cta-secondary"
              onClick={onTryDemo}
              data-testid="hero-demo-btn"
            >
              <Play size={18} />
              Try Demo
            </Button>
          </div>
          
          <p className="hero-demo-note">No account required. See it before you buy.</p>
        </div>
      </section>

      {/* Problem Section */}
      <section className="landing-problem">
        <div className="landing-section-content">
          <p className="problem-intro">
            Most businesses manage safety using paper forms, spreadsheets 
            or expensive subscription software.
          </p>
          <p className="problem-statement">None are ideal.</p>
          
          <div className="problem-list">
            {problems.map((problem, idx) => (
              <div key={idx} className="problem-item">
                <problem.icon size={20} strokeWidth={1.5} className="problem-icon" />
                <span>{problem.text}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Solution Section */}
      <section className="landing-solution">
        <div className="landing-section-content">
          <h2>The Essential Safety Tools You Need</h2>
          <p className="solution-intro">
            RISKEE provides the essential tools businesses need to manage safety 
            and keep records organised.
          </p>

          <div className="features-grid">
            {features.map((feature, idx) => (
              <div key={idx} className="feature-card">
                <feature.icon size={24} strokeWidth={1.5} />
                <span>{feature.name}</span>
              </div>
            ))}
          </div>

          <div className="solution-values">
            <span>No unnecessary complexity.</span>
            <span>No enterprise pricing.</span>
            <span>No lock-in.</span>
          </div>
        </div>
      </section>

      {/* Pricing Section */}
      <section className="landing-pricing">
        <div className="landing-section-content">
          <h2>One Payment</h2>
          
          <div className="pricing-card">
            <div className="pricing-amount">
              <span className="currency">$</span>
              <span className="amount">99</span>
              <span className="currency-label">AUD</span>
            </div>

            <div className="pricing-details">
              <span>No subscription.</span>
              <span>No monthly fees.</span>
              <span>No recurring charges.</span>
            </div>

            <p className="pricing-tagline">
              Buy it once.<br />
              Use it when you need it.
            </p>

            <div className="pricing-cta-group">
              <Button 
                size="lg" 
                className="pricing-cta"
                onClick={onGetStarted}
                data-testid="pricing-download-btn"
              >
                Download RISKEE
                <ArrowRight size={20} />
              </Button>
              <Button 
                size="lg" 
                variant="outline"
                className="pricing-cta-secondary"
                onClick={onTryDemo}
                data-testid="pricing-demo-btn"
              >
                <Play size={18} />
                Try Demo
              </Button>
            </div>
          </div>
          
          {/* Enterprise Note */}
          <div className="enterprise-note">
            <p>Need a larger solution?</p>
            <button 
              className="enterprise-link"
              onClick={() => setShowEnterpriseModal(true)}
              data-testid="enterprise-contact-btn"
            >
              <Building size={16} />
              Contact us for Enterprise pricing
            </button>
          </div>
        </div>
      </section>

      {/* Trust Section */}
      <section className="landing-trust">
        <div className="landing-section-content">
          <p className="trust-intro">
            Built for real businesses that need practical safety tools 
            without the cost and complexity of enterprise software.
          </p>
          
          <p className="trust-transparent">
            No demos behind contact forms. No sales calls required.<br />
            Try it yourself. Decide when you&apos;re ready.
          </p>

          <h3>Built on experience in:</h3>
          <div className="expertise-list">
            {expertise.map((item, idx) => (
              <span key={idx} className="expertise-item">{item}</span>
            ))}
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="landing-footer">
        <div className="landing-footer-content">
          <div className="footer-brand">
            <img src="/riskee_logo.png" alt="" className="footer-logo-img" />
            <span className="footer-logo-text">RISKEE</span>
          </div>
          <span className="footer-tagline">A Standards Protocol Platform</span>
        </div>
      </footer>

      {/* Enterprise Contact Modal */}
      <EnterpriseContactModal 
        isOpen={showEnterpriseModal} 
        onClose={() => setShowEnterpriseModal(false)} 
      />
    </div>
  );
};

export default LandingPage;
